import React, {useMemo, useState} from 'react'
import StatistikSpielerDialog from "./StatistikSpielerDialog";
import {getSpielerZeilen, legQuote, vorhanden, zahl} from "./statistikWerte";

const SPALTEN = [
    {id: "name", label: "Name", wert: zeile => zeile.name},
    {id: "spiele", label: "Spiele", sortierText: "Gewonnene Spiele", titel: "Gewonnen – Verloren, sortiert nach gewonnenen Spielen",
        wert: zeile => zeile.statistik.gewonneneSpiele,
        anzeige: s => s.gewonneneSpiele + "–" + s.verloreneSpiele + (s.unentschiedenSpiele > 0 ? "–" + s.unentschiedenSpiele : "")},
    {id: "legs", label: "Legs", sortierText: "Leg-Differenz", titel: "Gewonnen – Verloren, sortiert nach der Differenz",
        wert: zeile => zeile.statistik.gewonneneLegs - zeile.statistik.verloreneLegs,
        anzeige: s => s.gewonneneLegs + "–" + s.verloreneLegs},
    {id: "legquote", label: "Leg-%", sortierText: "Leg-Quote", titel: "Anteil gewonnener Legs",
        wert: zeile => legQuote(zeile.statistik), anzeige: s => legQuote(s) !== undefined ? zahl(legQuote(s), 0) + " %" : "–"},
    {id: "average", label: "Avg.", titel: "Average", wert: zeile => Number(zeile.statistik.average), anzeige: s => zahl(s.average)},
    {id: "first9Average", label: "F9-Avg.", titel: "First 9 Average", wert: zeile => Number(zeile.statistik.first9Average), anzeige: s => zahl(s.first9Average)},
    {id: "_100Plus", label: "100+", wert: zeile => zeile.statistik._100Plus, anzeige: s => s._100Plus},
    {id: "_140Plus", label: "140+", wert: zeile => zeile.statistik._140Plus, anzeige: s => s._140Plus},
    {id: "_171er", label: "171", wert: zeile => zeile.statistik._171er, anzeige: s => s._171er},
    {id: "_180er", label: "180", wert: zeile => zeile.statistik._180er, anzeige: s => s._180er},
];

/**
 * Statistik der Spieler im neuen Design: Kennzahlen des Events, sortierbare Tabelle und
 * Details je Spieler mit Diagrammen. Die alte Ansicht verwendet weiterhin PublicTournamentStatistikTab.
 */
export default function StatistikBereich({tournamentId, participants, gesamtStatistik, playerStatistiks}) {

    const [sortierung, setSortierung] = useState({spalte: "average", absteigend: true});
    // {name, statistik, playerId, mitBilanz}
    const [details, setDetails] = useState(undefined);

    const zeilen = useMemo(() => getSpielerZeilen(participants, playerStatistiks), [participants, playerStatistiks]);

    const sortierteZeilen = useMemo(() => {
        const spalte = SPALTEN.find(s => s.id === sortierung.spalte);
        const richtung = sortierung.absteigend ? -1 : 1;
        return [...zeilen].sort((a, b) => {
            if (spalte.id === "name") {
                return richtung * a.name.localeCompare(b.name);
            }
            // Spieler ohne Statistik bzw. ohne Wert immer ans Ende
            const wertA = vorhanden(a.statistik) ? spalte.wert(a) : undefined;
            const wertB = vorhanden(b.statistik) ? spalte.wert(b) : undefined;
            if (!vorhanden(wertA) || !vorhanden(wertB)) {
                return (vorhanden(wertA) ? 0 : 1) - (vorhanden(wertB) ? 0 : 1) || a.name.localeCompare(b.name);
            }
            return richtung * (wertA - wertB) || a.name.localeCompare(b.name);
        });
    }, [zeilen, sortierung]);

    const sortieren = (spalte) => {
        if (sortierung.spalte === spalte) {
            setSortierung({spalte, absteigend: !sortierung.absteigend});
        } else {
            // Namen von A-Z, Zahlen vom besten Wert an
            setSortierung({spalte, absteigend: spalte !== "name"});
        }
    }

    const oeffneSpieler = (zeile) => {
        if (vorhanden(zeile.statistik)) {
            setDetails({name: zeile.name, statistik: zeile.statistik, playerId: zeile.id, mitBilanz: true});
        }
    }

    const getEvent = () => {
        if (!vorhanden(gesamtStatistik)) {
            return null;
        }
        const kennzahl = (wert, bezeichnung) => <div className="dhpt-kennzahl"><b>{wert}</b><small>{bezeichnung}</small></div>;
        return <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel">
                <span>Event</span>
                <span className="dhpt-karte-zusatz">{participants.length} Teilnehmer</span>
            </h3>
            <div className="dhpt-kennzahlen">
                {kennzahl(zahl(gesamtStatistik.average), "Average")}
                {kennzahl(zahl(gesamtStatistik.first9Average), "First 9 Average")}
                {kennzahl(gesamtStatistik._100Plus, "100+")}
                {kennzahl(gesamtStatistik._140Plus, "140+")}
                {kennzahl(gesamtStatistik._171er, "171")}
                {kennzahl(gesamtStatistik._180er, "180")}
            </div>
            <button type="button" className="dhpt-text-knopf"
                    onClick={() => setDetails({name: "Event", statistik: gesamtStatistik, mitBilanz: false})}>
                <i className="pi pi-chart-bar" aria-hidden="true"/> Verteilung der Aufnahmen und Highlights
            </button>
        </section>
    }

    const getKopf = (spalte) => {
        const aktiv = sortierung.spalte === spalte.id;
        const icon = !aktiv ? "pi pi-sort-alt" : (sortierung.absteigend ? "pi pi-sort-amount-down" : "pi pi-sort-amount-up-alt");
        return <th key={spalte.id} scope="col" className={spalte.id === "name" ? "dhpt-st-name" : "dhpt-st-zahl"}
                   aria-sort={aktiv ? (sortierung.absteigend ? "descending" : "ascending") : "none"}>
            <button type="button" className="dhpt-st-sortierknopf" title={spalte.titel} onClick={() => sortieren(spalte.id)}>
                {spalte.label}<i className={icon} aria-hidden="true"/>
            </button>
        </th>
    }

    const getZeile = (zeile) => {
        const s = zeile.statistik;
        const klickbar = vorhanden(s);
        return <tr key={zeile.id} className={"dhpt-st-zeile" + (klickbar ? " dhpt-st-klickbar" : " dhpt-st-ohne")}
                   tabIndex={klickbar ? 0 : undefined}
                   onClick={() => oeffneSpieler(zeile)}
                   onKeyDown={(event) => {
                       if (event.key === "Enter" || event.key === " ") {
                           event.preventDefault();
                           oeffneSpieler(zeile);
                       }
                   }}>
            <td className="dhpt-st-name">{zeile.name}</td>
            {/* data-label: Beschriftung der Werte in der Kartenansicht auf dem Handy */}
            {SPALTEN.filter(spalte => spalte.id !== "name").map(spalte =>
                <td key={spalte.id} data-label={spalte.label}
                    className={"dhpt-st-zahl dhpt-st-" + spalte.id + (sortierung.spalte === spalte.id ? " dhpt-st-sortiert" : "")}>
                    {klickbar ? spalte.anzeige(s) : "–"}
                </td>
            )}
            <td className="dhpt-st-pfeil" aria-hidden="true">{klickbar && <i className="pi pi-chevron-right"/>}</td>
        </tr>
    }

    return <div className="dhpt-statistik">
        {getEvent()}
        {zeilen.length === 0 ?
            <p className="dhpt-hinweis">Es sind noch keine Teilnehmer vorhanden.</p>
            :
            <>
                <p className="dhpt-hinweis dhpt-st-nur-gross">Spalte antippen zum Sortieren, Spieler antippen für Details und Diagramme.</p>
                {/* auf dem Handy gibt es keine Spaltenköpfe, daher eine eigene Sortierung */}
                <div className="dhpt-st-sortierung">
                    <label className="dhpt-st-sortierung-feld">
                        <span>Sortieren nach</span>
                        <select value={sortierung.spalte} onChange={(event) => setSortierung({spalte: event.target.value, absteigend: event.target.value !== "name"})}>
                            {SPALTEN.map(spalte => <option key={spalte.id} value={spalte.id}>{spalte.sortierText || spalte.titel || spalte.label}</option>)}
                        </select>
                    </label>
                    <button type="button" className="dhpt-st-tauschen"
                            aria-label={sortierung.absteigend ? "Absteigend sortiert, umkehren" : "Aufsteigend sortiert, umkehren"}
                            onClick={() => setSortierung({...sortierung, absteigend: !sortierung.absteigend})}>
                        <i className={sortierung.absteigend ? "pi pi-sort-amount-down" : "pi pi-sort-amount-up-alt"} aria-hidden="true"/>
                    </button>
                </div>
                <p className="dhpt-hinweis dhpt-st-nur-klein">Spieler antippen für Details und Diagramme.</p>
                <div className="dhpt-st-rahmen">
                    <table className="dhpt-st-tabelle">
                        <caption className="dhpt-st-versteckt">Statistik der Spieler</caption>
                        <thead>
                        <tr>
                            {SPALTEN.map(getKopf)}
                            <th scope="col" className="dhpt-st-pfeil"><span className="dhpt-st-versteckt">Details</span></th>
                        </tr>
                        </thead>
                        <tbody>
                        {sortierteZeilen.map(getZeile)}
                        </tbody>
                    </table>
                </div>
            </>
        }
        {details !== undefined &&
            <StatistikSpielerDialog name={details.name} statistik={details.statistik} mitBilanz={details.mitBilanz}
                                    tournamentId={tournamentId} playerId={details.playerId} onClose={() => setDetails(undefined)}/>
        }
    </div>
}
